import { DateTime } from "luxon";
import { endOfDay, endOfMonth, endOfWeek, platoColorToHex, startOfDay, startOfMonth, startOfWeek } from "./calendar-utils";
import { fetchCalendarData, fetchCalendars, fetchCalendarSlots } from "@/lib/api/calendar";
import { isWhatsAppBridgeStubName } from "@/lib/messages/messages-helpers";
import { getAppointmentFilters } from "@/lib/stores/appointment-filters-store";
import { getUserPreferences } from "@/lib/stores/user-preference-store";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import { hashHueHex, isNilId, isPlatoCalendar, normalizeCalendar, standaloneCalendars } from "@/lib/calendars";

const EVENT_COLORS = ["blue", "indigo", "emerald", "purple", "pink", "orange", "cyan", "rose", "amber"];

function pickColor(value = "") {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return EVENT_COLORS[Math.abs(hash) % EVENT_COLORS.length];
}

function toDateTime(value) {
  const input = String(value || "").trim();
  if (!input) return null;

  // API sends ISO date strings with timezone offset included.
  const parsed = DateTime.fromISO(input, { setZone: true });
  if (!parsed.isValid) return null;
  return parsed;
}

function toDisplayName(entity, fallbackId) {
  if (entity && typeof entity.displayName === "string" && entity.displayName.trim()) {
    return entity.displayName.trim();
  }
  return String(fallbackId || "").trim();
}

function toDoctorName(entity, fallbackId) {
  if (!entity || typeof entity !== "object") {
    return toDisplayName(entity, fallbackId);
  }

  const firstName = toTrimmedString(entity.firstName);
  const lastName = toTrimmedString(entity.lastName);
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    return fullName;
  }

  return toDisplayName(entity, fallbackId);
}

/**
 * Resolve a person's display name from an enriched entity WITHOUT falling back to
 * an id. Returns "" when no human-readable name is available so callers can pick
 * a better fallback (e.g. the appointment title) than a raw ObjectId.
 */
function toPersonName(entity) {
  if (!entity || typeof entity !== "object") {
    return "";
  }

  const firstName = toTrimmedString(entity.firstName);
  const lastName = toTrimmedString(entity.lastName);
  const fullName = `${firstName} ${lastName}`.trim();
  const name = fullName || toTrimmedString(entity.displayName);
  // Unsynced WhatsApp-bridge patients carry their stub login id
  // (`ext-bot-<botId>-<phone>`) as the "name" — not a person's name.
  return isWhatsAppBridgeStubName(name) ? "" : name;
}

function toTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function toObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function normalizeHexColor(value) {
  const input = toTrimmedString(value);
  if (!input) {
    return "";
  }

  const normalized = input.startsWith("#") ? input.slice(1) : input;
  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    const expanded = normalized
      .split("")
      .map((char) => `${char}${char}`)
      .join("")
      .toUpperCase();
    return `#${expanded}`;
  }

  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `#${normalized.toUpperCase()}`;
  }

  return "";
}

function formatLocation(locationValue, fallback = "") {
  const asString = toTrimmedString(locationValue);
  if (asString) {
    return asString;
  }

  const location = toObject(locationValue);
  if (!location) {
    return fallback;
  }

  const preferredKeys = ["displayName", "name", "label", "address", "clinic", "room", "city", "country"];
  const picked = preferredKeys
    .map((key) => toTrimmedString(location[key]))
    .filter(Boolean);

  if (picked.length > 0) {
    return picked.join(" - ");
  }

  const firstStringValue = Object.values(location).find(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
  return toTrimmedString(firstStringValue) || fallback;
}

// Calendars rarely change during a session, so cache the calendars index for 2
// hours to avoid refetching every time the user navigates across weeks/months.
// Settings → Calendars calls invalidateCalendarsCache() after every mutation.
const CALENDARS_CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours
let calendarsCachePromise = null;
let calendarsCacheTs = 0;

function emptyCalendarIndex() {
  return { byCalendarId: new Map(), byDoctorId: new Map(), byId: new Map(), items: [] };
}

// Plato/blocking calendars first so a local non-blocking calendar can never shadow the
// doctor's Plato colour in the doctor-keyed fallback (first-wins below).
function calendarIndexRank(cal) {
  return (isPlatoCalendar(cal) ? 0 : 2) + (cal.nonBlocking ? 1 : 0);
}

function buildCalendarIndex(items) {
  const index = emptyCalendarIndex();
  if (!Array.isArray(items)) return index;
  const normalized = items
    .map((raw) => ({ raw, cal: normalizeCalendar(raw) }))
    .filter(({ cal }) => cal.id)
    .sort((a, b) => calendarIndexRank(a.cal) - calendarIndexRank(b.cal));
  for (const { raw, cal } of normalized) {
    const platoHex = normalizeHexColor(platoColorToHex(raw?.metadata?.sourceColor));
    const ownHex = normalizeHexColor(cal.colorHex);
    // Standalone calendars have no Plato colour: give them a deterministic hue so
    // entries, the editor's "Automatic" swatch, the facet dot and the column title agree.
    const standaloneHex = !cal.doctorId ? hashHueHex(cal.id) : "";
    const hex = platoHex || ownHex || standaloneHex;
    index.byId.set(cal.id, { ...cal, colorHex: hex || "" });
    index.items.push(raw);
    if (!hex) continue;
    index.byCalendarId.set(cal.id, hex);
    // Appointments often don't carry a calendarId, only doctorId — index by doctor too.
    if (cal.doctorId && !index.byDoctorId.has(cal.doctorId)) {
      index.byDoctorId.set(cal.doctorId, hex);
    }
  }
  return index;
}

async function loadCalendarIndex() {
  const now = Date.now();
  if (!calendarsCachePromise || now - calendarsCacheTs > CALENDARS_CACHE_TTL_MS) {
    calendarsCachePromise = fetchCalendars()
      .then((res) => buildCalendarIndex(res?.items))
      .catch(() => emptyCalendarIndex()); // fail-soft: items just fall back to the hash color
    calendarsCacheTs = now;
  }
  return calendarsCachePromise;
}

/** Drop the cached calendars index (Settings → Calendars mutations, realtime hints). */
export function invalidateCalendarsCache() {
  calendarsCachePromise = null;
  calendarsCacheTs = 0;
}

/** Normalized calendar (+ resolved colorHex) by id, or null. */
export async function getCalendarById(calendarId) {
  const id = toTrimmedString(calendarId);
  if (!id) return null;
  const index = await loadCalendarIndex();
  return index.byId.get(id) || null;
}

/**
 * Active standalone calendars as facet/column options:
 * `{ id, name, quickLabel, colorHex, localOnly, nonBlocking }`.
 */
export async function getStandaloneCalendarOptions() {
  const index = await loadCalendarIndex();
  return standaloneCalendars(index.items).map((cal) => ({
    id: cal.id,
    name: cal.name || "Calendar",
    quickLabel: cal.name || "Calendar",
    colorHex: index.byCalendarId.get(cal.id) || cal.colorHex,
    localOnly: cal.localOnly,
    nonBlocking: cal.nonBlocking,
  }));
}

function mapCalendarItemToEvent(item, calendarColors) {
  const timezone = String(item?.timezone || item?._tenant?.timezone || "").trim();
  const startDateTime = toDateTime(item?.start);
  const endDateTime = toDateTime(item?.end);
  const startAt = startDateTime ? startDateTime.toLocal() : null;
  const endAt = endDateTime ? endDateTime.toLocal() : null;
  // TODO: time format should be in the timezone of the event, not the timezone of the user ... but now for tests I will keep this as is ...
  // const startAt = startDateTime ? startDateTime : null;
  // const endAt = endDateTime ? endDateTime : null;
  if (!startAt || !endAt) {
    return null;
  }

  // console.log("startAt UTC: ", String(item?.start || "").trim(), "->", startAt.toUTC().toISO());
  // console.log("endAt UTC: ", String(item?.end || "").trim(), "->", endAt.toUTC().toISO());

  // FIXME: ... we need to handle all-day appointments differently? in UI ...
  if (item?.allDay === true) {
    return null;
  }

  const doctorName = toDoctorName(item?._doctor, item?.doctorId);
  const doctorNameShort = toDisplayName(item?._doctor, item?.doctorId);

  const tenantName = resolveTenantDisplayName(item?._tenant);
  const location = formatLocation(item?.location, tenantName || undefined);

  const baseTitle = String(item?.title || "").trim();
  // `_patient` isn't always enriched for migrated appointments. When the patient
  // record is missing, fall back to the human-readable appointment title rather
  // than surfacing the raw patient ObjectId in the UI.
  const patientName = toPersonName(item?._patient) || (String(item?.patientId || "").trim() ? baseTitle : "");
  // Patient date of birth ("YYYY-MM-DD", patients only) — drives the birthday
  // cue on appointment cards.
  const patientDob = toTrimmedString(item?._patient?.dob);
  const description = typeof item?.description === "string" ? item.description.trim() : "";
  const kind = String(item?.kind || "").trim();
  const type = String(item?.type || "").trim();
  const mode = String(item?.mode || "").trim();
  const link = String(item?.link || "").trim();
  const slotType = String(item?.slotType || "").trim();
  const metadata = toObject(item?.metadata) || undefined;
  const itemColorHex = normalizeHexColor(metadata?.colorHex);
  const calendarId = String(item?.calendarId || "").trim();
  // The zero ObjectID is the server's "no doctor / no clinic" sentinel (standalone
  // calendar entries); normalize it away so nothing treats it as a real owner.
  const doctorId = isNilId(item?.doctorId) ? "" : String(item.doctorId).trim();
  const tenantId = isNilId(item?.tenantId) ? "" : String(item.tenantId).trim();
  const calendarEntry = calendarId && calendarColors?.byId ? calendarColors.byId.get(calendarId) : null;
  // Fall back to the parent calendar's converted Plato sourceColor only when the item
  // didn't pick its own color. Item-level overrides still win. Appointments may not
  // carry a calendarId, so fall through to doctorId-keyed lookup.
  let calendarColorHex = "";
  if (!itemColorHex && calendarColors) {
    if (calendarId) calendarColorHex = calendarColors.byCalendarId.get(calendarId) || "";
    if (!calendarColorHex && doctorId) calendarColorHex = calendarColors.byDoctorId.get(doctorId) || "";
  }
  
  const colorHex = itemColorHex || calendarColorHex;
  const titleSeed = type || kind;
  const title = baseTitle || (titleSeed ? `${titleSeed[0].toUpperCase()}${titleSeed.slice(1)}` : "Calendar event");

  const id = String(item?.id || "");
  let mongoSlotId = String(item?.mongoSlotId || "").trim();
  if (!mongoSlotId && kind === "slot" && id.startsWith("s-")) {
    const idParts = id.split("-");
    if (idParts.length >= 2) mongoSlotId = idParts[1];
  }

  return {
    id,
    kind,
    mongoSlotId: mongoSlotId || undefined,
    type: type || undefined,
    title,
    description: description || undefined,
    mode: mode || undefined,
    link: link || undefined,
    patient: patientName || undefined,
    patientDob: patientDob || undefined,
    doctor: doctorName || undefined,
    doctorShort: doctorNameShort || undefined,
    location: location || undefined,
    colorHex: colorHex || undefined,
    // The explicit per-appointment override (metadata.colorHex) and the
    // calendar-inherited fallback, kept apart so the color editor knows what
    // is actually pinned vs merely inherited.
    colorHexOverride: itemColorHex || undefined,
    colorHexBase: calendarColorHex || undefined,
    color: pickColor(String(doctorId || item?.patientId || tenantId || item?.id || kind)),
    status: String(item?.status || "").trim() || undefined,
    startAt,
    endAt,
    timezone: timezone || undefined,
    slotType: slotType || undefined,
    episodeId: String(item?.episodeId || "").trim() || undefined,
    metadata,
    locationData: toObject(item?.location) || undefined,
    pricing: toObject(item?.pricing) || undefined,
    preVisit: toObject(item?.preVisit) || undefined,
    snapshots: toObject(item?.snapshots) || undefined,
    doctorId: doctorId || undefined,
    patientId: String(item?.patientId || "").trim() || undefined,
    // Server-computed "patient has no Plato record yet" flag; kept undefined
    // when absent so isNewPatient() falls back to the zero-id check.
    newPatient: typeof item?.newPatient === "boolean" ? item.newPatient : undefined,
    // Explicit lock-first-15-minutes pin (metadata.lock15m); undefined means
    // "default" — effectiveLock15m() then turns it on for 30-min appointments.
    lock15m: typeof metadata?.lock15m === "boolean" ? metadata.lock15m : undefined,
    // Appointment-confirmation state (metadata.confirmationStatus): "pending"
    // (confirmation sent, awaiting the patient) → "confirmed". Undefined/absent
    // means not requested. confirmedAt is when it was confirmed (display only).
    confirmationStatus: String(metadata?.confirmationStatus || "").trim() || undefined,
    confirmedAt: String(metadata?.confirmedAt || "").trim() || undefined,
    // Cancellation reason (metadata.cancellationReason), stamped when the
    // appointment was cancelled through the app — the bot relays the patient's
    // WhatsApp reason here. Display-only; shown on the cancelled card so staff
    // see why it was cancelled without opening the History panel.
    cancellationReason: String(metadata?.cancellationReason || "").trim() || undefined,
    tenantId: tenantId || undefined,
    calendarId: calendarId || undefined,
    calendarName: calendarEntry?.name || undefined,
    // Direct calendar entry (metadata.directEntry): created straight into a calendar,
    // usually doctor-less; never a "new patient".
    directEntry: metadata?.directEntry === true,
    _doctor: item?._doctor || undefined,
    _patient: item?._patient || undefined,
    _tenant: item?._tenant || undefined,
  };
}

/**
 * `includeSlots` overrides the user's "Slots" display setting for THIS fetch.
 * The month view passes false: it shows bookings only, so pulling a month of
 * open slots down the wire just to drop them is pure waste.
 */
async function fetchEventsByRange(start, end, { includeSlots } = {}) {
  const fromIso = start.toUTC().toISO();
  const toIso = end.toUTC().toISO();

  // FIXME: ... for now we don't need to fetch slots here ...
  /*
  const { tenantIds, doctorIds } = getAppointmentFilters();
  console.log("[calendar] fetching slots for", { fromIso, toIso, tenantIds, doctorIds });
  void fetchCalendarSlots({
    start: fromIso,
    end: toIso,
    ...(Array.isArray(tenantIds) && tenantIds.length > 0 ? { tenantIds } : {}),
    ...(Array.isArray(doctorIds) && doctorIds.length > 0 ? { doctorIds } : {}),
  })
    .then((slots) => {
      console.log("[calendar] available slots", slots);
    })
    .catch((err) => {
      console.warn("[calendar] fetchCalendarSlots failed", err);
    });
  */

  const { calendarIncludeNotAvailableSlots, calendarIncludeSlots } = getUserPreferences();
  const [response, calendarColors] = await Promise.all([
    fetchCalendarData({
      scope: "date",
      // FIXME: ... context will depend on the view type ...
      // scope: "doctor",
      from: fromIso,
      to: toIso,
      includeSlots: includeSlots ?? calendarIncludeSlots,
      includeNotAvailableSlots: calendarIncludeNotAvailableSlots,
      // Standalone-calendar direct entries (doctor-less) are opt-in: only the
      // desktop calendar renders them (their own column).
      includeStandalone: true,
      // calendarIds: ["15fdcb8e9c6f6ff46b458865"],
      // doctorId: ["ab5ee84e7a5f5813bffac59c"],
    }),
    loadCalendarIndex(),
  ]);

  // console.log("[calendar] kind items", (Array.isArray(response?.items) ? response.items : []).filter(item => item?.kind === "slot"));

  const sourceItems = Array.isArray(response?.items) ? response.items : [];
  return sourceItems.map((item) => mapCalendarItemToEvent(item, calendarColors)).filter(Boolean);
}

// A doctorId → hex map of each doctor's calendar color (converted Plato
// `sourceColor`), reusing the same 2-hour-cached calendars fetch that the event
// loader uses. Doctors with no colored calendar simply aren't in the map. The
// returned Map is a copy so callers can't mutate the shared cache.
export async function getDoctorColorMap() {
  const colors = await loadCalendarIndex();
  return new Map(colors.byDoctorId);
}

export async function getCalendarEvents(referenceDate = DateTime.now()) {
  return fetchEventsByRange(startOfMonth(referenceDate), endOfMonth(referenceDate));
}

export async function getEventsForDay(date) {
  return fetchEventsByRange(startOfDay(date), endOfDay(date));
}

export async function getEventsForWeek(anchorDate) {
  return fetchEventsByRange(startOfWeek(anchorDate), endOfWeek(anchorDate));
}

/**
 * Arbitrary day span — the week view renders more than its anchor week (see
 * getWeekDayWindow), so it fetches the whole rendered run rather than a week.
 */
export async function getEventsForDayRange(firstDay, lastDay) {
  return fetchEventsByRange(startOfDay(firstDay), endOfDay(lastDay));
}

export async function getEventsForMonth(anchorDate, options) {
  return fetchEventsByRange(startOfMonth(anchorDate), endOfMonth(anchorDate), options);
}

export async function getTodayEvents(referenceDate = DateTime.now()) {
  return fetchEventsByRange(startOfDay(referenceDate), endOfDay(referenceDate));
}
